import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherTrash2 } from "@subframe/core";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useDeleteEventRecordCommandControllerRemove } from "@/generated/event-records/event-records";
import type { EventRecordListItemDto } from "@/generated/model/eventRecordListItemDto";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";

interface DeleteEventRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: EventRecordListItemDto;
  onDeleted: () => void;
}

/**
 * Confirms permanent deletion of an event record (NAU-341).
 *
 * Unlike every other destructive dialog in the app, this one requires the user to
 * type the date the record was filed before the button enables. Deleting an event
 * record destroys Ofsted inspection evidence and cannot be undone by anyone, so a
 * single mis-click should not be enough — and the token has to identify the
 * record, not merely its type (see below).
 */
export function DeleteEventRecordDialog({
  open,
  onOpenChange,
  record,
  onDeleted,
}: DeleteEventRecordDialogProps) {
  const deleteRecord = useDeleteEventRecordCommandControllerRemove();
  const [confirmation, setConfirmation] = useState("");

  /**
   * The date the record was filed. The event TYPE name would be the obvious token,
   * but every fire drill shares it — typing "Fire Drill / Evacuation" confirms
   * "I mean to delete a fire drill", never "I mean to delete THIS one", which is
   * exactly the mistake the dialog exists to catch. `submittedAt` is the only
   * per-record value on the row a person can reasonably read and retype.
   */
  const token = format(new Date(record.submittedAt), "d MMM yyyy");
  const confirmed = confirmation.trim().toLowerCase() === token.toLowerCase();

  const handleConfirm = async () => {
    try {
      await deleteRecord.mutateAsync({ eventRecordId: record.id });
      toast.success(`"${record.eventTypeName}" has been permanently deleted`);
      onDeleted();
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(
          error,
          `Failed to delete "${record.eventTypeName}"`,
        ),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">Delete event record</DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <IconWithLogoBackground
              variant="error"
              size="x-large"
              icon={<FeatherTrash2 />}
            />
            <div className="flex flex-col items-center gap-2">
              <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                Delete this event permanently?
              </span>
              <span className="text-center text-body font-body text-subtext-color">
                This removes the <strong>{record.eventTypeName}</strong> record
                and its acknowledgement history from the system entirely. It is
                inspection evidence and cannot be recovered by anyone. Consider
                archiving it instead — archived events stay in History.
              </span>
            </div>
            <div className="flex w-full flex-col gap-2">
              {/* The label lives on TextField (which renders a <label>) so the
                  input has an accessible name. No placeholder: that would put the
                  answer inside the field the user is meant to read and retype. */}
              <TextField
                className="h-auto w-full"
                variant="outline"
                size="base"
                label={`Type the date it was filed (${token}) to confirm`}
              >
                <TextField.Input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                />
              </TextField>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={deleteRecord.isPending}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="destructive-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={deleteRecord.isPending || !confirmed}
            >
              {deleteRecord.isPending
                ? "Deleting..."
                : "Yes, delete permanently"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
